import React from "react";
import { FiPlus, FiEdit2, FiBook, FiUsers } from "react-icons/fi";

const ProgramList = ({
  loading,
  saving,
  programs,
  programTypes,
  onEdit,
  onStatusChange,
  onCreateClick,
}) => {
  const getProgramTypeName = (programTypeId) => {
    const type = programTypes.find((t) => t._id === programTypeId);
    return type ? `[${type.code}] ${type.name}` : "Unknown Type";
  };

  const getProgramTypeCode = (programTypeId) => {
    const type = programTypes.find((t) => t._id === programTypeId);
    return type ? type.code : "N/A";
  };

  const handleStatusChange = async (program, newStatus) => {
    if (
      window.confirm(
        `Are you sure you want to ${
          newStatus === "active" ? "activate" : "deactivate"
        } this program?`
      )
    ) {
      await onStatusChange(program, newStatus);
    }
  };

  if (loading) {
    return (
      <div className="bg-white shadow-md rounded-xl border border-gray-200 p-8 text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
        <p className="mt-3 text-sm text-gray-600 font-medium">
          Loading programs...
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-xl rounded-2xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-800 tracking-tight">
          Program Management
          <span className="ml-2 text-sm font-medium text-gray-500">
            ({programs.length})
          </span>
        </h2>

        <button
          type="button"
          onClick={onCreateClick}
          disabled={saving || programTypes.length === 0}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition disabled:opacity-50"
          title={programTypes.length === 0 ? "Create program types first" : ""}
        >
          <FiPlus className="w-4 h-4" />
          Add Program
        </button>
      </div>

      {/* Empty State */}
      {programs.length === 0 ? (
        <div className="p-16 text-center">
          <FiBook className="mx-auto text-5xl text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900">
            No programs found
          </h3>
          <p className="text-sm text-gray-600 mt-1 mb-6">
            {programTypes.length === 0
              ? "Create program types first to begin adding programs."
              : "Start by creating your first program."}
          </p>
          <button
            type="button"
            onClick={onCreateClick}
            disabled={saving || programTypes.length === 0}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
          >
            <FiPlus />
            Create Program
          </button>
        </div>
      ) : (
        <div className="divide-y">
          {programs.map((program) => (
            <div
              key={program._id}
              className="p-6 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  {/* Title Section */}
                  <div className="flex items-center flex-wrap gap-3 mb-3">
                    <span className="px-2.5 py-1 rounded-md text-xs font-bold bg-indigo-100 text-indigo-700 border border-indigo-200">
                      {getProgramTypeCode(
                        typeof program.programTypeId === "object"
                          ? program.programTypeId._id
                          : program.programTypeId
                      )}
                    </span>

                    <h3 className="text-lg font-semibold text-gray-900 truncate">
                      {program.name}
                    </h3>

                    {/* Program Code Badge */}
                    <span className="px-2 py-0.5 rounded bg-gray-200 text-xs font-mono text-gray-700 border border-gray-300">
                      {program.code}
                    </span>

                    <span
                      className={`px-2.5 py-1 rounded-md text-xs font-semibold ${
                        program.status === "active"
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      {program.status}
                    </span>
                  </div>

                  {/* Program Type Name */}
                  <div className="flex items-center gap-2 text-sm text-gray-600 font-medium mb-2">
                    <FiBook className="w-4 h-4 opacity-60" />
                    {getProgramTypeName(
                      typeof program.programTypeId === "object"
                        ? program.programTypeId._id
                        : program.programTypeId
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-3 ml-6">
                  <button
                    type="button"
                    onClick={() => onEdit(program)}
                    disabled={saving}
                    title="Edit Program"
                    className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 transition disabled:opacity-50"
                  >
                    <FiEdit2 className="w-4 h-4" />
                  </button>

                  {program.status === "active" ? (
                    <button
                      type="button"
                      onClick={() => handleStatusChange(program, "inactive")}
                      disabled={saving}
                      title="Deactivate Program"
                      className="p-2 rounded-lg bg-red-100 hover:bg-red-200 text-red-600 transition disabled:opacity-50"
                    >
                      <FiUsers className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleStatusChange(program, "active")}
                      disabled={saving}
                      title="Activate Program"
                      className="p-2 rounded-lg bg-green-100 hover:bg-green-200 text-green-600 transition disabled:opacity-50"
                    >
                      <FiUsers className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProgramList;
